/**
 * Asset Loader — Cache-first loading with Draco support, progress tracking,
 * retry, and abort.
 *
 * Wraps Three.js loaders (GLTFLoader, HDRLoader, TextureLoader) and
 * integrates with AssetCache for deduplication and fast re-use.
 *
 * @see docs/ims-engine-api/04-asset-pipeline.md
 */

import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';
import { TextureLoader } from 'three';

export type { GLTF };

import type { AssetCache } from './AssetCache';
import type { LoadingPhase, AssetLoadedInfo } from '../api/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Options for individual load calls.
 * All fields are optional — sensible defaults apply.
 */
export interface LoadOptions {
  /** Loading phase for cache priority. Default: 'lazy' */
  phase?: LoadingPhase;
  /** AbortSignal to cancel the request. */
  signal?: AbortSignal;
  /** Progress callback per-asset (0–1). */
  onProgress?: (ratio: number) => void;
  /** Number of retries on network failure. Default: 1 */
  retries?: number;
}

/** Options for the AssetLoader constructor. */
export interface AssetLoaderOptions {
  /** Path to Draco decoder WASM files (enables Draco-compressed GLB). */
  dracoDecoderPath?: string;
  /** Callback when any asset finishes loading (for event emission). */
  onAssetLoaded?: (info: AssetLoadedInfo) => void;
}

/** Aggregated multi-asset progress tracking. */
interface ProgressSlot {
  weight: number;
  ratio: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Result from a raw Three.js loader — carries data + byte size from XHR. */
interface RawLoadResult<T = unknown> {
  data: T;
  /** Total bytes from Content-Length, or 0 if unavailable. */
  sizeBytes: number;
}

/** Check if an error is a network/abort error worth retrying. */
function isRetryable(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false;
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('404') || msg.includes('403')) return false;
  }
  return true;
}

/** Sleep utility for retry backoff. */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// AssetLoader
// ---------------------------------------------------------------------------

export class AssetLoader {
  private _cache: AssetCache;
  private _gltfLoader: GLTFLoader;
  private _hdrLoader: HDRLoader;
  private _textureLoader: TextureLoader;
  private _dracoLoader: DRACOLoader | null = null;
  private _onAssetLoaded: ((info: AssetLoadedInfo) => void) | null;
  private _disposed = false;

  /** Active AbortControllers for in-flight requests. */
  private _activeRequests = new Set<AbortController>();

  /** Multi-asset progress tracking. */
  private _progressSlots = new Map<string, ProgressSlot>();
  private _onAggregateProgress: ((ratio: number) => void) | null = null;

  constructor(cache: AssetCache, options?: AssetLoaderOptions) {
    this._cache = cache;
    this._onAssetLoaded = options?.onAssetLoaded ?? null;

    // --- GLTFLoader + optional Draco ---
    this._gltfLoader = new GLTFLoader();
    if (options?.dracoDecoderPath) {
      this._dracoLoader = new DRACOLoader();
      this._dracoLoader.setDecoderPath(options.dracoDecoderPath);
      this._gltfLoader.setDRACOLoader(this._dracoLoader);
    }

    // --- Other loaders ---
    this._hdrLoader = new HDRLoader();
    this._textureLoader = new TextureLoader();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Load a GLB model. Returns parsed GLTF object.
   * Cache-first: if the asset is already cached, returns immediately.
   */
  async loadGLB(url: string, opts: LoadOptions = {}): Promise<GLTF> {
    const data = await this._loadWithCache(url, 'glb', opts, (resolvedUrl, onProgress) =>
      this._loadGLTFRaw(resolvedUrl, onProgress),
    );
    return data as GLTF;
  }

  /**
   * Load an HDR environment map. Returns a DataTexture.
   * Cache-first: returns from cache if available.
   */
  async loadHDR(url: string, opts: LoadOptions = {}): Promise<unknown> {
    return this._loadWithCache(url, 'hdr', opts, (resolvedUrl, onProgress) =>
      this._loadRGBERaw(resolvedUrl, onProgress),
    );
  }

  /**
   * Load a standard texture (jpg/png/webp).
   * Cache-first: returns from cache if available.
   */
  async loadTexture(url: string, opts: LoadOptions = {}): Promise<unknown> {
    return this._loadWithCache(url, 'texture', opts, (resolvedUrl, onProgress) =>
      this._loadTextureRaw(resolvedUrl, onProgress),
    );
  }

  // -------------------------------------------------------------------------
  // Multi-Asset Progress
  // -------------------------------------------------------------------------

  /**
   * Register a set of URLs for aggregate progress tracking.
   * Call before loading. The callback receives 0–1 as assets complete.
   */
  startProgressGroup(
    urls: string[],
    onProgress: (ratio: number) => void,
  ): void {
    this._progressSlots.clear();
    const weight = 1 / urls.length;
    for (const url of urls) {
      this._progressSlots.set(url, { weight, ratio: 0 });
    }
    this._onAggregateProgress = onProgress;
  }

  /** Clear progress group tracking. */
  endProgressGroup(): void {
    this._progressSlots.clear();
    this._onAggregateProgress = null;
  }

  // -------------------------------------------------------------------------
  // Abort & Dispose
  // -------------------------------------------------------------------------

  /** Abort all in-flight requests. */
  abortAll(): void {
    for (const controller of this._activeRequests) {
      controller.abort();
    }
    this._activeRequests.clear();
  }

  /** Dispose all loaders and abort in-flight requests. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this.abortAll();
    this._dracoLoader?.dispose();
    this.endProgressGroup();
  }

  /** Whether this loader has been disposed. */
  get isDisposed(): boolean {
    return this._disposed;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /**
   * Cache-first load pattern:
   * 1. Check cache → return immediately if hit
   * 2. Load from network via loadFn
   * 3. Store in cache
   * 4. Emit assetLoaded event
   */
  private async _loadWithCache(
    url: string,
    type: 'glb' | 'hdr' | 'texture',
    opts: LoadOptions,
    loadFn: (url: string, onProgress: (ratio: number) => void) => Promise<RawLoadResult>,
  ): Promise<unknown> {
    if (this._disposed) {
      throw new Error('AssetLoader is disposed');
    }

    // --- Cache hit ---
    const cached = this._cache.get(url);
    if (cached) {
      this._updateProgress(url, 1);
      opts.onProgress?.(1);
      this._emitAssetLoaded(url, cached.sizeKB, 0, true);
      return cached.data;
    }

    // --- Network load with retry ---
    const phase = opts.phase ?? 'lazy';
    const maxRetries = opts.retries ?? 1;
    const startTime = performance.now();

    // Track so we can abort on dispose
    const controller = new AbortController();
    this._activeRequests.add(controller);

    // Merge external signal
    if (opts.signal) {
      opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      if (controller.signal.aborted) {
        this._activeRequests.delete(controller);
        throw new DOMException('Aborted', 'AbortError');
      }

      try {
        const result = await loadFn(url, (ratio) => {
          this._updateProgress(url, ratio);
          opts.onProgress?.(ratio);
        });

        this._activeRequests.delete(controller);

        const { data, sizeBytes } = result;
        const sizeKB = Math.round(sizeBytes / 1024);
        this._cache.set(url, type, data, sizeKB, phase);
        // NOTE: We intentionally do NOT call acquire() here.
        // acquire/release is an explicit reference-counting API for consumers
        // that manage asset lifetimes (e.g. ObjectManager pinning a GLB while
        // it is in-scene).  Automatically acquiring on every load caused a
        // memory leak: refCount only grew because no consumer called release(),
        // permanently pinning assets and defeating LRU eviction.

        const durationMs = Math.round(performance.now() - startTime);
        this._updateProgress(url, 1);
        opts.onProgress?.(1);
        this._emitAssetLoaded(url, sizeKB, durationMs, false);

        return data;
      } catch (err) {
        lastError = err;

        // Don't retry aborts or definitive HTTP errors
        if (!isRetryable(err) || attempt === maxRetries) {
          this._activeRequests.delete(controller);
          throw err;
        }

        // Exponential backoff: 500ms, 1000ms, ...
        await sleep(500 * (attempt + 1));
      }
    }

    // Should never reach here, but just in case
    this._activeRequests.delete(controller);
    throw lastError;
  }

  /** Emit the assetLoaded callback. */
  private _emitAssetLoaded(
    url: string,
    sizeKB: number,
    durationMs: number,
    cached: boolean,
  ): void {
    this._onAssetLoaded?.({
      id: url,
      url,
      cached,
      sizeKB,
      durationMs,
    });
  }

  /** Update per-asset progress and compute aggregate. */
  private _updateProgress(url: string, ratio: number): void {
    const slot = this._progressSlots.get(url);
    if (!slot || !this._onAggregateProgress) return;
    slot.ratio = ratio;

    let aggregate = 0;
    for (const s of this._progressSlots.values()) {
      aggregate += s.weight * s.ratio;
    }
    this._onAggregateProgress(Math.min(1, aggregate));
  }

  // -------------------------------------------------------------------------
  // Raw Three.js loader wrappers
  // -------------------------------------------------------------------------

  private _loadGLTFRaw(
    url: string,
    onProgress: (ratio: number) => void,
  ): Promise<RawLoadResult<GLTF>> {
    return new Promise<RawLoadResult<GLTF>>((resolve, reject) => {
      let totalBytes = 0;
      this._gltfLoader.load(
        url,
        (gltf) => resolve({ data: gltf, sizeBytes: totalBytes }),
        (event) => {
          if (event.lengthComputable) {
            totalBytes = event.total;
            onProgress(event.loaded / event.total);
          }
        },
        (error) => reject(error),
      );
    });
  }

  private _loadRGBERaw(
    url: string,
    onProgress: (ratio: number) => void,
  ): Promise<RawLoadResult> {
    return new Promise((resolve, reject) => {
      let totalBytes = 0;
      this._hdrLoader.load(
        url,
        (texture) => resolve({ data: texture, sizeBytes: totalBytes }),
        (event) => {
          if (event.lengthComputable) {
            totalBytes = event.total;
            onProgress(event.loaded / event.total);
          }
        },
        (error) => reject(error),
      );
    });
  }

  private _loadTextureRaw(
    url: string,
    onProgress: (ratio: number) => void,
  ): Promise<RawLoadResult> {
    return new Promise((resolve, reject) => {
      let totalBytes = 0;
      this._textureLoader.load(
        url,
        (texture) => resolve({ data: texture, sizeBytes: totalBytes }),
        (event) => {
          if (event.lengthComputable) {
            totalBytes = event.total;
            onProgress(event.loaded / event.total);
          }
        },
        (error) => reject(error),
      );
    });
  }
}
